import type { PCPStepConfig } from '@/constants/pcp/types';
import { formatStringToSha256Hash } from '@/utils/formatStringToSha256Hash';
import { Typography } from '@mui/material';
import { useContext, useMemo } from 'react';
import Stages from '../../components/components/Stages';
import Steps from '../../components/components/Steps';
import WhatToBring from '../../components/components/WhatToBring';
import { PCPDataContext } from '../../PCPortal';
import DassForm from '../steps/doctor/DassForm';
import DoctorConsult from '../steps/doctor/DoctorConsult';

export default function PreDoctor() {
  const { userData } = useContext(PCPDataContext);

  const isInitialNurseConsultBooked = userData?.preNurseConsultation.completed;
  const isDoctorConsultBooked = userData?.preDoctorConsultation.consultationBooked;
  const isDoctorConsultCompleted = userData?.preDoctorConsultation.completed;
  const hasDoctorBookingUrl = userData?.preDoctorConsultation.calcomBookingUrl !== null;
  const shaUserEmail = formatStringToSha256Hash(userData?.prefillFormData.email);
  const isPatientIntiatedDocBooking =
    isInitialNurseConsultBooked && !isDoctorConsultBooked && !isDoctorConsultCompleted && hasDoctorBookingUrl;

  const doctorStepsConfig: PCPStepConfig[] = useMemo(
    () => [
      {
        stepComponent: <DassForm />,
        stepId: 'dass_form',
        title: 'DASS form',
        buttonId: 'dass-form-step-button',
        requiredApiPath: 'preDoctorConsultation.dassForm.required',
        urlApiPath: 'preDoctorConsultation.dassForm.formUrl',
        completedApiPath: 'preDoctorConsultation.dassForm.completed',
        subtitleActive: '3 mins to complete',
        buttonTextActive: 'Complete now',
        buttonTextCompleted: 'Completed',
        ga4Events: {
          buttonClickEvents: [
            {
              event: 'dass_form_start',
              clickId: 'dass-form-step-button',
              dlv_click_id: 'dass-form-step-button',
              event_category: 'button-click',
            },
          ],
        },
      },
      {
        stepComponent: <DoctorConsult />,
        stepId: 'doctor_consult',
        title: 'Doctor consultation',
        buttonId: 'view-doctor-consult-step-button',
        urlApiPath: 'preDoctorConsultation.calcomBookingUrl',
        completedApiPath: 'preDoctorConsultation.completed',
        subtitleActive: isPatientIntiatedDocBooking ? 'Make a booking' : 'View and reschedule',
        buttonTextActive: isPatientIntiatedDocBooking ? 'Book consultation' : 'View consultation',
        buttonTextCompleted: 'Completed',
        ga4Events: {
          buttonClickEvents: isPatientIntiatedDocBooking
            ? [
                {
                  event: 'ga4_3__start_booking',
                  event_category: 'cal.com',
                  event_label: 'Doctor Consultation',
                  promocode: '',
                  sha_email: shaUserEmail || '',
                },
              ]
            : [
                {
                  event: 'click_view_doctor_consultation',
                  clickId: 'view-doctor-consult-step-button',
                  dlv_click_id: 'view-doctor-consult-step-button',
                  event_category: 'button-click',
                },
              ],
        },
      },
    ],
    [isPatientIntiatedDocBooking],
  );
  return (
    <>
      <Typography variant="h5" component="h1" sx={(theme) => ({ color: theme.palette.primary.main })}>
        Your Alternaleaf journey
      </Typography>

      <Stages stage={2} />

      <Typography variant="body1" mb="1.5rem">
        You&apos;re all set for your doctor consultation!
      </Typography>

      <Steps stepsConfig={doctorStepsConfig} />

      <WhatToBring />

      <Typography
        variant="h5"
        component="h2"
        sx={(theme) => ({ color: theme.palette.primary.main, marginBottom: theme.spacing(6) })}
      >
        What to expect after your doctor consultation
      </Typography>
      <Typography variant="body1" mb="1rem">
        If you qualify after the evaluation, your doctor will create a personalised treatment plan.
      </Typography>
      <Typography variant="body1">
        You will be given access to a patient portal where you can view your prescriptions, purchase your products and
        manage your future bookings.
      </Typography>
    </>
  );
}
